import { ref } from 'vue';
import { api, type Exploration } from '../api.js';
import type { ExplorationSpec } from '../lib/metric-exploration.js';

export type SavedExploration = Exploration<ExplorationSpec>;

/**
 * Ordering for the "Saved explorations" menu: favorites first (alphabetical),
 * then the rest (alphabetical) — identical contract to `sortFilterSets` so the
 * saved-thing lists across the app behave the same (spec §8.2).
 */
export function sortExplorations(
  items: SavedExploration[],
  favoriteIds: number[],
): SavedExploration[] {
  const favored = new Set(favoriteIds);
  const favorites = items
    .filter((s) => favored.has(s.id))
    .sort((a, b) => a.name.localeCompare(b.name));
  const rest = items.filter((s) => !favored.has(s.id)).sort((a, b) => a.name.localeCompare(b.name));
  return [...favorites, ...rest];
}

/**
 * Reactive wrapper around the org `explorations` list + the caller's
 * `favoriteExplorations` user setting. Star toggles + deletes are optimistic
 * (rolled back on failure), mirroring `useFilterSets` exactly.
 */
export function useExplorations() {
  const items = ref<SavedExploration[]>([]);
  const favorites = ref<number[]>([]);
  const loading = ref(false);
  const error = ref(false);

  async function load(): Promise<void> {
    loading.value = true;
    try {
      const [list, favs] = await Promise.all([
        api.explorations.list() as Promise<SavedExploration[]>,
        api.getFavoriteExplorations(),
      ]);
      items.value = list;
      favorites.value = favs;
      error.value = false;
    } catch {
      error.value = true;
    } finally {
      loading.value = false;
    }
  }

  /** Upsert-by-name (server semantics); refreshes the list on success. */
  async function save(name: string, spec: ExplorationSpec): Promise<{ id: number }> {
    const res = await api.explorations.save(name, spec);
    await load();
    return res;
  }

  async function remove(id: number): Promise<void> {
    const previous = items.value;
    items.value = items.value.filter((s) => s.id !== id); // optimistic
    try {
      await api.explorations.delete(id);
    } catch (e) {
      items.value = previous; // roll back on failure
      throw e;
    }
  }

  async function toggleFavorite(id: number): Promise<void> {
    const previous = favorites.value;
    const next = previous.includes(id) ? previous.filter((v) => v !== id) : [...previous, id];
    favorites.value = next; // optimistic
    try {
      await api.putFavoriteExplorations(next);
    } catch (e) {
      favorites.value = previous; // roll back on failure
      throw e;
    }
  }

  return { items, favorites, loading, error, load, save, remove, toggleFavorite };
}
